// src/components/Settings/PasswordPanel.tsx
import React, { useState } from 'react';
import { changePassword } from '../../api/userService';

interface PasswordPanelProps {
  t: Function;
  refreshUserData: () => Promise<void>;
  setShowNotification: React.Dispatch<React.SetStateAction<boolean>>;
  setNotificationMessage: React.Dispatch<React.SetStateAction<string>>;
}

const PasswordPanel: React.FC<PasswordPanelProps> = ({
  t,
  refreshUserData,
  setShowNotification,
  setNotificationMessage
}) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatNewPassword, setRepeatNewPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSavePassword = async () => {
    setErrorMessage('');

    // Validación: Las nuevas contraseñas deben coincidir
    if (newPassword !== repeatNewPassword) {
      setErrorMessage(t('settings.errors.passwordsNoMatch'));
      return;
    }

    // Validación: Longitud mínima de 8 caracteres
    if (newPassword.length < 8) {
      setErrorMessage(t('settings.errors.minPasswordLength'));
      return;
    }

    try {
      // changePassword lanzará un error si response.data.error existe (ver userService)
      await changePassword(oldPassword, newPassword);
      await refreshUserData();

      setNotificationMessage(t('settings.notifications.passwordUpdated'));
      setShowNotification(true);

      setOldPassword('');
      setNewPassword('');
      setRepeatNewPassword('');
    } catch (err: any) {
      console.error('Error al cambiar contraseña:', err);
      // Convertir el mensaje de error del backend al idioma usando las claves de traducción
      if (err.message === "Incorrect old password") {
        setErrorMessage(t('settings.errors.invalidOldPassword'));
      } else {
        setErrorMessage(t('settings.errors.updatePassword'));
      }
    }
  };

  return (
    <div className="bg-[#0f172a] text-white rounded-lg shadow-md p-6 w-full md:w-1/2">
      <h3 className="text-lg font-bold mb-4">{t('settings.changePasswordTitle')}</h3>

      {errorMessage && (
        <div className="text-red-500 mb-2">{errorMessage}</div>
      )}

      <div className="mb-4">
        <label className="flex flex-col text-sm">
          {t('settings.labels.oldPassword')}:
          <input
            type="password"
            className="bg-gray-700 text-white border border-gray-600 p-2 rounded mt-1"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
          />
        </label>
      </div>
      <div className="mb-4">
        <label className="flex flex-col text-sm">
          {t('settings.labels.newPassword')}:
          <input
            type="password"
            className="bg-gray-700 text-white border border-gray-600 p-2 rounded mt-1"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </label>
        {/* Párrafo de ayuda */}
        <p className="text-gray-400 text-xs mt-1">
          {t('settings.labels.passwordHint')}
        </p>
      </div>
      <div className="mb-4">
        <label className="flex flex-col text-sm">
          {t('settings.labels.repeatNewPassword')}:
          <input
            type="password"
            className="bg-gray-700 text-white border border-gray-600 p-2 rounded mt-1"
            value={repeatNewPassword}
            onChange={(e) => setRepeatNewPassword(e.target.value)}
          />
        </label>
      </div>

      <button
        onClick={handleSavePassword}
        className="border rounded bg-gray-800 text-white px-4 py-2 hover:bg-gray-700"
      >
        {t('settings.buttons.savePassword')}
      </button>
    </div>
  );
};

export default PasswordPanel;
